import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import CodeIcon from "@mui/icons-material/Code"
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined"
import SubjectIcon from "@mui/icons-material/Subject"
import TagIcon from "@mui/icons-material/Tag"
import type { SvgIconTypeMap } from "@mui/material"
import type { OverridableComponent } from "@mui/material/OverridableComponent"
import { v4 as uuidv4 } from "uuid"

import type { DeploymentSchemaFeatures } from "../../types/generated/api/DeploymentSchemaFeatures"
import { WorkflowInputFeature } from "../../types/generated/api/WorkflowInputFeature"
import type { NodeSimpleChipProps } from "../Workflows/types/interfaces"
import { COMPONENT_TYPES, type Schema } from "./Interfaces"

export const typeCastBasedOnType = (value: unknown, type: WorkflowInputFeature.type): unknown => {
  switch (type) {
    case WorkflowInputFeature.type.NUMBER:
      return isNaN(Number(value)) ? 0 : Number(value)
    case WorkflowInputFeature.type.BOOLEAN:
      // Convert value to string, make it lowercase, and compare with "true"
      // This handles various boolean string representations ("True", "TRUE", "true")
      // Returns true only if the lowercase value exactly matches "true", false otherwise
      return String(value).toLowerCase() === "true"
    default:
      return value
  }
}

/**
 * Determines the appropriate schema type for a given value
 * @param value - Value to analyze
 * @returns {WorkflowInputFeature.type} The inferred schema type
 */
export const inferTypeBasedOnValue = (value: unknown): WorkflowInputFeature.type => {
  // Check for array type first
  if (Array.isArray(value)) return WorkflowInputFeature.type.LIST
  if (value === null) return WorkflowInputFeature.type.UNDEFINED

  const type = typeof value
  switch (type) {
    case "boolean":
      return WorkflowInputFeature.type.BOOLEAN
    case "number":
      return WorkflowInputFeature.type.NUMBER
    case "string":
      return WorkflowInputFeature.type.TEXT
    case "undefined":
      return WorkflowInputFeature.type.UNDEFINED
    default:
      // Default to TEXT type for unknown values
      return WorkflowInputFeature.type.TEXT
  }
}

/**
 * Converts JSON data to table schema format
 * @param jsonData - Raw JSON data to convert
 * @param workflowFeatures - Available workflow features for reference
 * @returns {Record<string, SchemaFeature & { value: unknown }>} Converted schema data
 */
export const convertJsonToTableSchema = (
  jsonData: Record<string, unknown>,
  workflowFeatures?: WorkflowInputFeature[] | DeploymentSchemaFeatures[],
): Schema => {
  const newData: Schema = {}

  // Convert each JSON entry to schema format
  for (const [key, value] of Object.entries(jsonData)) {
    newData[key] = {
      value: value,
      name: key,
      type: inferTypeBasedOnValue(value),
      id: uuidv4(), // Generate unique ID for each feature
      new: !workflowFeatures?.find((feature) => feature.name === key), // Mark as new if not in workflow
    }
  }

  return newData
}

/**
 * Returns a default value based on the feature type
 * @param type - The schema feature type
 * @returns A type-appropriate default value
 */
export const dummyValueBasedOnType = (type: WorkflowInputFeature.type): unknown => {
  switch (type) {
    case WorkflowInputFeature.type.BOOLEAN:
      return true

    case WorkflowInputFeature.type.LIST:
      return [1, 2, 3]

    case WorkflowInputFeature.type.NUMBER:
      return 0

    case WorkflowInputFeature.type.TEXT:
      return "string"

    case WorkflowInputFeature.type.DATE:
      return "2024-10-10"
  }
}

/**
 * Converts a flat JSON object with dot-separated keys into a nested object structure.
 *
 * For example, given an input like:
 *   { "a.b.c": 1, "a.b.d": 2, "x": 3 }
 * The function will produce:
 *   { "a": { "b": { "c": 1, "d": 2 } }, "x": 3 }
 *
 * @param json - A stringified JSON object with dot-separated keys.
 * @returns A stringified JSON object with nested structure, or undefined if input is empty or invalid.
 */
export const convertNestedFeatureStringsToObjects = (json: string): string => {
  if (!json) return "{}"

  const parsedJson = JSON.parse(json)
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const newFixedJson: Record<string, any> = {}

  for (const key of Object.keys(parsedJson)) {
    const keys = key.split(".") // split all nested keys
    let current = newFixedJson
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i]
      if (typeof k === "string" && k !== undefined) {
        if (!current[k] || typeof current[k] !== "object") {
          current[k] = {}
        }
        current = current[k]
      }
    }
    const lastKey = keys[keys.length - 1]
    if (typeof lastKey === "string" && lastKey !== undefined) {
      current[lastKey] = parsedJson[key]
    }
  }

  return JSON.stringify(newFixedJson, null, "\t")
}

export function getComponentTypeFromNode(
  node: NodeSimpleChipProps,
): (typeof COMPONENT_TYPES)[keyof typeof COMPONENT_TYPES] | null {
  const { data } = node

  if (data.ruleset) return COMPONENT_TYPES.RULESET
  if (data.program) return COMPONENT_TYPES.PROGRAM
  if (data.calculator) return COMPONENT_TYPES.CALCULATOR
  if (data.taglist) return COMPONENT_TYPES.TAGLIST
  if (data.scorecard_set) return COMPONENT_TYPES.SCORECARDSET
  return null
}

export const getComponentDataFromType = (
  componentType: (typeof COMPONENT_TYPES)[keyof typeof COMPONENT_TYPES],
):
  | {
      Logo: OverridableComponent<SvgIconTypeMap<{}, "svg">>
      logoClassName: string
    }
  | undefined => {
  switch (componentType) {
    case COMPONENT_TYPES.RULESET:
      return {
        Logo: RuleOutlinedIcon,
        logoClassName: "RulesetCardType",
      }
    case COMPONENT_TYPES.SCORECARDSET:
      return {
        Logo: AssignmentOutlinedIcon,
        logoClassName: "ScorecardsetCardType",
      }
    case COMPONENT_TYPES.SCORECARD:
      return {
        Logo: AssignmentOutlinedIcon,
        logoClassName: "ScorecardsetCardType",
      }
    case COMPONENT_TYPES.SCORETABLE:
      return {
        Logo: AssignmentOutlinedIcon,
        logoClassName: "ScorecardsetCardType",
      }
    case COMPONENT_TYPES.CALCULATOR:
      return {
        Logo: CodeIcon,
        logoClassName: "CalculatorCardType",
      }
    case COMPONENT_TYPES.PROGRAM:
      return {
        Logo: SubjectIcon,
        logoClassName: "ProgramCardType",
      }
    case COMPONENT_TYPES.TAGLIST:
      return {
        Logo: TagIcon,
        logoClassName: "TaglistCardType",
      }
    default:
      return undefined
  }
}

export const getApiComponentType = (
  componentType: (typeof COMPONENT_TYPES)[keyof typeof COMPONENT_TYPES],
): "programs" | "rulesets" | "taglists" | "scorecardsets" | "scorecards" | "calculators" => {
  switch (componentType) {
    case COMPONENT_TYPES.PROGRAM:
      return "programs"
    case COMPONENT_TYPES.RULESET:
      return "rulesets"
    case COMPONENT_TYPES.TAGLIST:
      return "taglists"
    case COMPONENT_TYPES.SCORECARDSET:
      return "scorecardsets"
    case COMPONENT_TYPES.SCORECARD:
      return "scorecards"
    case COMPONENT_TYPES.SCORETABLE:
      return "scorecards"
    case COMPONENT_TYPES.CALCULATOR:
      return "calculators"
  }
}
